import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const FAQ = [
  {
    question: "What is battery backup monitoring?",
    answer:
      "Battery backup monitoring is the practice of tracking residential battery state, capacity, health, and projected energy availability. For installers, it means seeing capacity in kWh, health scores, degradation trends, and overnight reserve risk across the fleet so you can spot issues before warranty claims or customer calls.",
  },
  {
    question: "Do you show capacity in kWh or just percent?",
    answer:
      "Watt Tower shows energy in kWh where it matters. You see remaining and usable energy, not just state-of-charge percent. Capacity tracking (daily kWh, degradation trend) supports warranty documentation and early visibility into underperformance.",
  },
  {
    question: "Can I use this for warranty claims?",
    answer:
      "Yes. Watt Tower tracks battery capacity over time, health scores, cycle counts (measured and estimated), and warranty projection timelines. You can generate one-click PDF documentation for warranty claims so you have structured evidence instead of scattered OEM screenshots.",
  },
  {
    question: "Which battery systems are supported?",
    answer:
      "Watt Tower supports residential solar and battery systems connected via Sol-Ark, EG4, and SolarEdge inverters. We focus on hybrid and off-grid residential installations. Credentials are read-only, encrypted, and revocable.",
  },
];

export default function BatteryBackupMonitoring() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </Helmet>
      <article className="mx-auto max-w-3xl px-4 py-16 lg:px-8">
        <h1 className="mb-6 font-sans text-3xl font-bold md:text-4xl">
          Battery Backup Monitoring Software
        </h1>
        <p className="mb-10 font-sans text-lg leading-relaxed text-wt-text-secondary">
          Residential battery monitoring software for installers: capacity
          tracking in kWh, health scores, overnight reserve forecasting, and
          fleet-level visibility. Built for companies managing solar and
          battery systems at scale.
        </p>

        <section className="space-y-10">
          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Why Installers Need Battery Monitoring
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Batteries degrade over time. Without battery backup monitoring,
            you often find out when a warranty claim is denied or a customer
            complains. Watt Tower gives installers daily capacity tracking
            (kWh, not just percent), health scores, degradation trends, and
            cycle counts so you have early visibility and structured
            documentation for warranty conversations.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Capacity and Health in One Place
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Our battery monitoring software shows remaining and usable energy
            in kWh, health score, degradation trend, and cycle counts
            (measured and estimated). Warranty projection timelines help you
            plan. One-click PDF exports support warranty claims with
            reproducible, data-backed evidence instead of OEM screenshots.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Overnight Reserve Forecasting
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Battery backup monitoring is not only about capacity—it is about
            whether the system will have enough energy by sunrise. Watt Tower
            forecasts projected kWh at sunrise with explicit confidence bands
            and flags “At Risk” at the fleet level. Installers can act before
            the customer runs out of power.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Fleet-Level Battery Visibility
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            From the installer dashboard you see how many systems are live,
            how many are at overnight reserve risk, and which have battery
            health or capacity issues. Filter by provider, tag, or grid mode.
            Drill into any property for full telemetry and alert history.
            Battery backup monitoring at scale replaces scattered OEM apps.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Built for Sol-Ark and Hybrid Systems
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Watt Tower supports Sol-Ark, EG4, and SolarEdge. Residential
            battery monitoring works for hybrid and off-grid systems.
            Read-only access; credentials encrypted and revocable. We observe;
            we do not command.
          </p>
        </section>

        <section className="mt-16 border-t border-wt-border-default pt-12">
          <h2 className="mb-8 font-sans text-2xl font-semibold text-wt-text-primary">
            Frequently Asked Questions
          </h2>
          <dl className="space-y-6">
            {FAQ.map((item) => (
              <div key={item.question}>
                <dt className="font-sans text-base font-semibold text-wt-text-primary">
                  {item.question}
                </dt>
                <dd className="mt-2 font-sans text-base leading-relaxed text-wt-text-secondary">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <a href="/#installer-access">
            <Button className="h-11 bg-primary px-6 font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted">
              Request Installer Access
            </Button>
          </a>
          <Link to="/">
            <Button
              variant="outline"
              className="h-11 border-wt-border-default bg-transparent font-sans text-wt-text-primary hover:bg-white/5"
            >
              Back to home
            </Button>
          </Link>
        </div>
        <p className="mt-6 font-sans text-sm text-wt-text-muted">
          See also:{" "}
          <Link
            to="/solar-monitoring-software-for-installers"
            className="text-primary hover:underline"
          >
            solar monitoring software for installers
          </Link>
          ,{" "}
          <Link
            to="/solar-fleet-dashboard"
            className="text-primary hover:underline"
          >
            solar fleet dashboard
          </Link>
          ,{" "}
          <Link
            to="/solar-forecasting-software"
            className="text-primary hover:underline"
          >
            solar forecasting software
          </Link>
          .
        </p>
      </article>
    </PublicLayout>
  );
}
